import {createUseStyles} from 'react-jss'
import {titleMarginBottom} from '../constants/layout'
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator'
import React, {useState} from "react";
import useFetchClient from "../hooks/useFetchClient";
import {restConfig} from "../authConfig";
import Button from "../components/Button";
import Card from "../components/Card";
import {Alert, Col, Container, Row} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faPiggyBank, faTimesCircle} from '@fortawesome/free-solid-svg-icons'
import {Colors} from "../constants/colors";
import InputField from "../components/InputField";
import {SwishPayoutStatusResponse} from "../redux/types";

const SwishStatus = () => {
  const styles = useStyles()
  const fetchClient = useFetchClient()
  const [payoutIdentifier, setPayoutIdentifier] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<SwishPayoutStatusResponse | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  const querySwishStatus = (payoutIdentifier: string) => {
    const url = restConfig.routes.swish + '/' + payoutIdentifier
    setLoading(true)
    setError(null)

    fetchClient
      .get(url)
      .then(response => setResult(response.data as SwishPayoutStatusResponse))
      .catch(error => {
        console.log('Failed to query swish payout:', error)
        setError(error.toString());
        setResult(undefined)
      })
      .finally(() => setLoading(false))
  }

  const renderResult = () => {
    if (!result) {
      return null
    }

    return (
      <Row>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Status for {result.payoutIdentifier}</h2>
            <FontAwesomeIcon
              size={'2x'}
              icon={result.success ? faCheckCircle : faTimesCircle}
              color={result.success ? Colors.euro_700 : Colors.rubel_700}
            />
          </div>
          <p><strong>Result:</strong> {result.success ? ' Success' : ' Failed'}</p>
          <p><strong>Response code:</strong> {result.responseCode}</p>
          <p><strong>Error message:</strong> {result.errorMessage || '-'}</p>
          {
            !!result.payout &&
              <Card className={styles.subCard}>
                  <h3>Payout details</h3>
                  <p><strong>Payout ID:</strong> {result.payout.payoutInstructionUuid}</p>
                  <p><strong>Reference:</strong> {result.payout.payerPaymentReference}</p>
                  <p><strong>Payee Alias:</strong> {result.payout.payeeAlias}</p>
                  <p><strong>Amount:</strong> {result.payout.amount} {result.payout.currency}</p>
                  <p><strong>Status:</strong> {result.payout.status}</p>
                  <p><strong>Created:</strong> {result.payout.dateCreated}</p>
                  <p><strong>Paid:</strong> {result.payout.datePaid || '-'}</p>
                  <p><strong>Message:</strong> {result.payout.message || '-'}</p>
                {result.payout.errorCode && (
                  <>
                    <p><strong>Error Code:</strong> {result.payout.errorCode}</p>
                    <p><strong>Error Message:</strong> {result.payout.errorMessage}</p>
                  </>
                )}
              </Card>
          }
        </Card>
      </Row>
    )
  }

  const renderError = () => {
    if (!error) {
      return null
    }

    return (
      <Row>
        <Alert
          className={styles.alert}
          variant={"danger"}>
          {error}
        </Alert>
      </Row>
    )
  }

  return (
    <Container className={styles.container}>
      <h1>Swish Status check</h1>

      <Row className={styles.row}>
        <Col xs={"auto"}>
          <FontAwesomeIcon
            icon={faPiggyBank}
            color={Colors.darkGray3}
            size='5x'
          />
        </Col>
        <Col>
          <p>
            Check the status of a Swish transaction. You need the Swish Payout Identifier to do this.
          </p>
        </Col>
      </Row>

      <Row className={styles.row}>
        <Card className={styles.card}>
          <InputField
            id='message'
            className={styles.inputField}
            label='Swish Payout Identifier'
            placeholder={'00000000-0000-0000-0000-000000000000'}
            value={payoutIdentifier}
            onChange={(event) => setPayoutIdentifier(event.target.value)}
          />

          <Button
            title={"Check"}
            disabled={payoutIdentifier.length < 1}
            onClick={() => querySwishStatus(payoutIdentifier)}
          />
        </Card>
      </Row>

      {renderResult()}
      {renderError()}

      <FullScreenLoadingIndicator visible={loading}/>
    </Container>
  )
}

export default SwishStatus

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    flex: 1
  },
  row: {
    flex: 1,
    justifyContent: "left",
    alignItems: "center",
    gap: 20,
    marginBottom: 15
  },
  alert: {
    flexGrow: 1
  },
  card: {
    flexGrow: 1,
    padding: 15
  },
  subCard: {
    flexGrow: 1,
    padding: 15,
    backgroundColor: Colors.darkGray5
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  inputField: {
    marginBottom: 15
  }
})
